import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { FinanceChannelFieldAvailability } from '@/api/generated/model';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { formatCompactMoney } from '@/lib/money';
import { financeColor } from '../lib/financeColors';
import { NotReadyBadge, NotReadyNote } from './FinanceNotReady';

export type PayoutStatusSlice = {
  key: string;
  label: string;
  amount: number | null;
  availability: FinanceChannelFieldAvailability;
};

/** Confluence §13 Creator Payout Summary — status donut (On Hold may be shell). */
export function PayoutStatusSummaryCard({
  currency,
  slices,
  note,
}: {
  currency: string;
  slices: PayoutStatusSlice[];
  note?: string;
}) {
  const isDark = useIsDarkMode();
  const liveSlices = slices.filter(
    (slice) => slice.availability === 'AVAILABLE' && slice.amount != null,
  );
  const total = liveSlices.reduce((sum, slice) => sum + (slice.amount ?? 0), 0);
  const anyShell = slices.some((slice) => slice.availability !== 'AVAILABLE');

  const option = useMemo(() => {
    const ink = {
      primary: tone(CHART_INK.primary, isDark),
      muted: tone(CHART_INK.muted, isDark),
      surface: tone(CHART_INK.surface, isDark),
      grid: tone(CHART_INK.grid, isDark),
    };
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: ink.surface,
        borderColor: ink.grid,
        textStyle: { color: ink.primary },
        formatter: (params: { name: string; value: number; percent: number }) =>
          `${params.name}: ${formatCompactMoney(params.value, currency)} (${params.percent.toFixed(1)}%)`,
      },
      series: [
        {
          type: 'pie',
          radius: ['48%', '72%'],
          avoidLabelOverlap: true,
          label: { show: false },
          data: liveSlices.map((slice) => ({
            name: slice.label,
            value: slice.amount ?? 0,
            itemStyle: { color: financeColor(slice.key) },
          })),
        },
      ],
    };
  }, [currency, isDark, liveSlices]);

  return (
    <Card className="shadow-none">
      <CardHeader className="space-y-1 p-5 pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-sm font-medium text-sky-400">
            Creator Payout Summary · {currency}
          </CardTitle>
          {anyShell ? (
            <NotReadyBadge
              state="partial"
              reason="On Hold is not ready — no transfer status enum yet. Paid / Pending / Failed are live when transfers exist."
            />
          ) : null}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Paid / Pending / Failed from transfer status. On Hold shells until an enum exists.
        </p>
      </CardHeader>
      <CardContent className="grid items-center gap-5 p-5 pt-2 lg:grid-cols-[minmax(0,1fr)_190px]">
        {liveSlices.length === 0 || total === 0 ? (
          <div className="flex h-[220px] items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
            No confirmed transfers in this window.
          </div>
        ) : (
          <ReactECharts option={option} notMerge style={{ height: 220, width: '100%' }} />
        )}
        <div className="grid gap-3">
          {slices.map((slice) => {
            const live = slice.availability === 'AVAILABLE' && slice.amount != null;
            return (
              <div key={slice.key} className="flex items-center justify-between gap-2 text-xs">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: financeColor(slice.key) }}
                    aria-hidden
                  />
                  <span className="truncate">{slice.label}</span>
                  {!live ? (
                    <NotReadyBadge
                      state="not-ready"
                      reason={
                        slice.key === 'ON_HOLD'
                          ? 'On Hold has no transfer status enum yet.'
                          : `${slice.label} shows when matching transfers exist in this window.`
                      }
                    />
                  ) : null}
                </span>
                <span className={live ? 'tabular-nums' : 'text-muted-foreground'}>
                  {live ? formatCompactMoney(slice.amount!, currency) : '—'}
                </span>
              </div>
            );
          })}
          {anyShell ? (
            <NotReadyNote>
              On Hold is not ready yet — no transfer status enum. Paid / Pending / Failed are live
              when transfers exist.
            </NotReadyNote>
          ) : null}
          {note ? <p className="text-[10px] leading-4 text-muted-foreground">{note}</p> : null}
        </div>
      </CardContent>
    </Card>
  );
}
